import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * S-8 base field — a bordered "combobox" trigger with an optional label on top
 * and a value / placeholder below (Skyscanner-style). Used by DateField,
 * TimeField and any option-selector that opens a popover/sheet.
 *
 * sizes:
 *  - "lg" : h-14 md:h-16, label on top + value below (standalone primary fields)
 *  - "md" : h-11, single centered value + trailing icon (dense inline rows, e.g. time ranges)
 */
export const FieldBox = React.forwardRef(function FieldBox(
  {
    label,
    value,
    placeholder,
    icon: Icon,
    onClick,
    active = false,
    error = false,
    disabled = false,
    size = 'lg',
    className,
    valueClassName,
    testId,
    ...rest
  },
  ref
) {
  const hasValue = value !== undefined && value !== null && value !== '';
  const isLg = size === 'lg';
  const a11yName = [label, hasValue ? value : placeholder].filter(Boolean).join(': ');

  return (
    <button
      ref={ref}
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={typeof a11yName === 'string' ? a11yName : undefined}
      data-testid={testId}
      data-active={active ? 'true' : undefined}
      className={cn(
        'group relative w-full bg-white text-left rounded-xl border outline-none',
        'flex flex-col justify-center',
        isLg ? 'h-14 md:h-16 px-3.5 md:px-4 py-2' : 'h-11 px-3',
        'transition-colors duration-150',
        !disabled && 'hover:border-slate-300',
        active && !error && 'border-[#FF6B35] ring-2 ring-[#FF6B35]/20',
        !active && !error && 'border-slate-200',
        error && 'border-red-500 ring-2 ring-red-500/20 bg-red-50',
        disabled && 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed',
        className
      )}
      {...rest}
    >
      {isLg && label ? (
        <span aria-hidden="true" className="block text-[11px] md:text-xs font-semibold uppercase tracking-wide text-slate-500 leading-none mb-1 truncate">
          {label}
        </span>
      ) : null}
      <span aria-hidden="true" className="flex items-center gap-2 min-w-0">
        <span
          className={cn(
            'flex-1 min-w-0 truncate',
            isLg ? 'text-sm md:text-base' : 'text-sm',
            hasValue ? 'font-semibold text-slate-900' : 'font-medium text-slate-400',
            valueClassName
          )}
        >
          {hasValue ? value : placeholder}
        </span>
        {Icon ? (
          <Icon
            className={cn(
              'flex-none text-slate-400 group-hover:text-slate-500 transition-colors',
              isLg ? 'h-4 w-4 md:h-[18px] md:w-[18px]' : 'h-4 w-4'
            )}
          />
        ) : null}
      </span>
    </button>
  );
});

export default FieldBox;
